import React, { useEffect, useState } from "react"
import { Steps } from "../journey/step-library"
import { H2, Container, FlexContainer, Body } from "@carrotfertility/carotene"
import ArrowSubmitButton from "components/carrot-plans/shared/ArrowSubmitButton"
import { useMedicalInfo } from "../../context/MedicalInfoContext"
import { Skip } from "../../../views/fle/Molecules"
import { useStateMachine } from "../../../context/stateMachine/StateMachineContext"
import { useJourneyFlow } from "../../shared/JourneyFlowContainer"
import ButtonSelectGroup from "../../shared/ButtonSelectGroup"
import { useMedicalInfoWorkflow } from "../../hooks/useMedicalInfoFlow"
import useButtonMultiSelect from "../../hooks/useButtonMultiSelect"
import { FemaleConditionsOptions, MaleConditionsOptions } from "./MedicalInfoOptions"
import { Spacer } from "../../../views/atoms/Atoms"
import { SelectButton } from "../../../views/atoms/buttons/SelectButton"
import { ClickExpandStyledInput, ClickExpandStyledInputBox } from "../../../views/atoms/forms/StyledInputs"

const step = Steps.MEDICAL_CONDITIONS

export default function ConditionsStep(): JSX.Element {
  const title = "Do you have any of these conditions?"
  const { IsAMAB, IsAFAB } = useMedicalInfoWorkflow()
  const { medicalInfo, updateMedicalInfo, hasQuestionBeenSkipped } = useMedicalInfo()
  const { handleContinueLoading, continueLoading } = useJourneyFlow()
  const defaultConditionsValues = IsAMAB ? medicalInfo?.maleConditions : IsAFAB ? medicalInfo?.femaleConditions : ""
  const defaultConditionsOtherValue = medicalInfo?.conditionsOther
  const buttonOptions = IsAMAB ? MaleConditionsOptions : IsAFAB ? FemaleConditionsOptions : null
  const { setNextStep } = useStateMachine(
    step,
    defaultConditionsValues || defaultConditionsOtherValue || hasQuestionBeenSkipped(step) ? "" : null
  )

  const [otherValue, setOtherValue] = useState(medicalInfo?.conditionsOther)
  const [showOtherValueInput, setShowOtherValueInput] = useState(!!medicalInfo?.conditionsOther)
  const somethingElseTitle = "Something else you'd like us to know about"

  useEffect(() => {
    setOtherValue(medicalInfo?.conditionsOther)
    setShowOtherValueInput(!!medicalInfo?.conditionsOther)
  }, [medicalInfo?.conditionsOther])

  const buttonControls = useButtonMultiSelect({
    options: buttonOptions,
    defaultValues: defaultConditionsValues || ""
  })

  async function onClickContinue(): Promise<void> {
    const selected = buttonControls.multiSelected
    const medicalInfoPayload = {
      conditionsOther: otherValue
    }

    if (IsAMAB) {
      medicalInfoPayload["maleConditions"] = selected
    } else if (IsAFAB) {
      medicalInfoPayload["femaleConditions"] = selected
    }

    await handleContinueLoading(() => updateMedicalInfo(medicalInfoPayload))
    setNextStep(step, "")
  }

  async function onSkip(): Promise<void> {
    await handleContinueLoading(() => updateMedicalInfo({ skipFields: [step] }))
    setNextStep(step, "")
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        onClickContinue()
      }}
    >
      <Container stack="large">
        <H2 stack="tiny">{title}</H2>
        <Body>Select any that apply.</Body>
      </Container>
      <Container>
        <ButtonSelectGroup loading={continueLoading} {...buttonControls} />
        {showOtherValueInput ? (
          <ClickExpandStyledInputBox style={{ marginTop: "4px" }}>
            <p style={{ padding: "0px 24px" }}>{somethingElseTitle}</p>
            <Spacer height={0.5} />
            <ClickExpandStyledInput
              id="otherValue"
              placeholder="Optional details"
              value={otherValue}
              onChange={(e) => {
                setOtherValue(e.target.value)
              }}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              maxLength={200}
            />
            <div className="flex flex-row-reverse justify-between items-center" style={{ margin: "8px 24px 0px 24px" }}>
              {otherValue && otherValue.length >= 200 * 0.75 && <p className="black-40">{otherValue.length}/200</p>}
            </div>
          </ClickExpandStyledInputBox>
        ) : (
          <div key={"otherValue"} className="flex flex-row justify-end w-100 mb1" style={{ marginTop: "4px" }}>
            <SelectButton
              outline={"otherValue" === otherValue}
              onClick={() => setShowOtherValueInput(true)}
              type="button"
              disabled={continueLoading}
            >
              {somethingElseTitle}
            </SelectButton>
          </div>
        )}
      </Container>
      <FlexContainer paddingTopBottom="huge" justifyContent="space-between">
        <Skip onClick={onSkip} onClickOnly enabled={!continueLoading} />
        <ArrowSubmitButton enabled={!continueLoading}>Continue</ArrowSubmitButton>
      </FlexContainer>
    </form>
  )
}
